import apiClient from './client'
import config from '../config/api'

/**
 * List directories at a given path
 * @param path Path to list directories in
 * @returns Array of directory names
 */
export const listDirectories = async (path: string): Promise<string[]> => {
  try {
    // Safety check - ensure path is a string and doesn't contain objects
    if (typeof path !== 'string') {
      console.error(`📕 Invalid path type: ${typeof path}`);
      return [];
    }
    
    if (path.includes('{') || path.includes('[object')) {
      console.error(`📕 Invalid path with object: ${path}`);
      return [];
    }
    
    console.log(`📗 files.listDirectories called with path: ${path}`);
    
    const requestUrl = `/list_dirs?path=${encodeURIComponent(path)}`;
    console.log(`📗 Making API request to:`, requestUrl);
    console.log(`📗 Full URL will be: ${config.API_URL}${requestUrl}`);
    
    const response = await apiClient.get(requestUrl);
    
    console.log(`📗 API response status:`, response.status);
    console.log(`📗 API response data:`, response.data);
    
    if (response.data && Array.isArray(response.data)) {
      return response.data;
    }
    
    console.warn(`📙 API returned non-array data:`, response.data);
    return [];
  } catch (error) {
    console.error(`📕 Error listing directories for path ${path}:`, error);
    if (error && typeof error === 'object' && 'response' in error) {
      const axiosError = error as any;
      console.error(`📕 Response status:`, axiosError.response?.status);
      console.error(`📕 Response data:`, axiosError.response?.data);
    }
    
    // Fallback data for the hierarchical directory structure
    // Level 1: Model names
    if (path === 'models/man') {
      return ['John']  // Male model names based on actual directory structure
    } else if (path === 'models/woman') {
      return ['Sarah', 'Emily']  // Female model names
    } 
    // Level 2: Pose types for each model
    else if (path === 'models/man/John') {
      return ['front', 'pose']  // Pose types based on actual directory structure
    }
    else if (path.match(/^models\/(man|woman)\/[^\/]+$/)) {
      return ['front', 'back', 'side']  // Pose types for other models
    } 
    // Level 3: Garment types for each pose
    else if (path === 'models/man/John/front') {
      return ['shirt', 't-shirt']  // Actual directory structure
    }
    else if (path.match(/^models\/(man|woman)\/[^\/]+\/[^\/]+$/)) {
      // Different garment types based on gender
      if (path.startsWith('models/man')) {
        return ['shirt', 't-shirt', 'jacket', 'casual']
      } else {
        return ['dress', 'blouse', 'skirt', 't-shirt']
      }
    }
    return [];
  }
}

/**
 * List files at a given path
 * @param path Path to list files in
 * @returns Array of file names
 */
export const listFiles = async (path: string): Promise<string[]> => {
  try {
    // Safety check - ensure path is a string and doesn't contain objects
    if (typeof path !== 'string') {
      console.error(`📕 Invalid path type for listFiles: ${typeof path}`);
      return [];
    }
    
    if (path.includes('{') || path.includes('[object')) {
      console.error(`📕 Invalid path with object in listFiles: ${path}`);
      return [];
    }
    
    console.log(`📗 files.listFiles called with path: ${path}`);
    const response = await apiClient.get(`/list_files?path=${encodeURIComponent(path)}`)
    console.log(`📗 files.listFiles response:`, response.data);
    return response.data || []
  } catch (error) {
    console.error(`📕 Error listing files for path ${path}:`, error)
    
    // Fallback data for image paths in the hierarchical structure
    // Level 3: Images for specific model+pose+garment combinations (most specific)
    if (path.match(/^models\/man\/[^\/]+\/front\/shirt$/)) {
      return ['model1.png', 'model2.png', 'model3.png', 'model4.png', 'model5.png'].map(img => `/${path}/${img}`)
    } else if (path.match(/^models\/man\/[^\/]+\/front\/t-shirt$/)) {
      return ['casual1.png', 'casual2.png', 'casual3.png'].map(img => `/${path}/${img}`)
    } else if (path.match(/^models\/woman\/[^\/]+\/front\/dress$/)) {
      return ['elegant1.png', 'elegant2.png', 'casual1.png', 'formal1.png'].map(img => `/${path}/${img}`)
    } else if (path.match(/^models\/woman\/[^\/]+\/front\/blouse$/)) {
      return ['modern-office1.png', 'modern-office2.png', 'casual1.png'].map(img => `/${path}/${img}`)
    }
    
    // Level 2: Default images for any garment type
    else if (path.match(/^models\/(man|woman)\/[^\/]+\/[^\/]+\/[^\/]+$/)) {
      return ['image1.png', 'image2.png', 'image3.png'].map(img => `/${path}/${img}`)
    }
    
    // Level 1: Sample images for pose directories
    else if (path.match(/^models\/(man|woman)\/[^\/]+\/front$/)) {
      return ['pose-preview1.png', 'pose-preview2.png'].map(img => `/${path}/${img}`)
    } 
    else if (path.match(/^models\/(man|woman)\/[^\/]+\/back$/)) {
      return ['back-preview1.png'].map(img => `/${path}/${img}`)
    }
    else if (path.match(/^models\/(man|woman)\/[^\/]+\/side$/)) {
      return ['side-preview1.png'].map(img => `/${path}/${img}`)
    }
    
    // Level 0: Sample images for model directories (thumbnail of the model)
    else if (path.match(/^models\/man\/John$/)) {
      return [`/${path}/model-preview.png`]
    }
    else if (path.match(/^models\/woman\/Sarah$/)) {
      return [`/${path}/model-preview.png`]
    }
    else if (path.match(/^models\/(man|woman)\/[^\/]+$/)) {
      return [`/${path}/preview.png`]
    }
    
    return []
  }
}

/**
 * Upload a file to the server
 * @param file File to upload
 * @param endpoint API endpoint to use
 * @param params Additional parameters
 * @returns Response from the server
 */
export const uploadFile = async (
  file: File,
  endpoint: string,
  params: Record<string, string>
): Promise<{ status: string, message?: string, path?: string }> => {
  try {
    const formData = new FormData()
    formData.append('file', file)
    
    // Add query parameters to the URL
    const queryParams = new URLSearchParams(params).toString()
    const url = `${endpoint}?${queryParams}`
    
    console.log(`Uploading file to: ${config.API_URL}/${url}`)
    
    // Use consistent credentials approach with the rest of the app
    const response = await fetch(`${config.API_URL}/${url}`, {
      method: 'POST',
      body: formData,
      credentials: 'include',  // Enable cookies for CORS requests
    })
    
    if (!response.ok) {
      console.error(`Upload failed with status: ${response.status}`)
      const errorText = await response.text()
      console.error(`Error response: ${errorText}`)
      return { 
        status: 'error', 
        message: `Server returned ${response.status}: ${errorText || 'Unknown error'}` 
      }
    }
    
    return response.json()
  } catch (error) {
    console.error('File upload error:', error)
    return { 
      status: 'error', 
      message: error instanceof Error ? error.message : 'Unknown error during upload' 
    }
  }
}

/**
 * Upload a custom model
 * @param file File to upload
 * @param username Username
 * @param project Project name
 * @returns Response from the server
 */
export const uploadCustomModel = async (
  file: File,
  username: string,
  project: string
): Promise<{ status: string, message?: string, path?: string }> => {
  return uploadFile(file, 'upload_custom_model', { username, project })
}

/**
 * Upload a garment
 * @param file File to upload
 * @param username Username
 * @param project Project name
 * @returns Response from the server
 */
export const uploadGarment = async (
  file: File,
  username: string,
  project: string
): Promise<{ status: string, message?: string, path?: string }> => {
  return uploadFile(file, 'upload_garment', { username, project })
}

export default {
  listDirectories,
  listFiles,
  uploadCustomModel,
  uploadGarment
}
